import { FUNCTIONS_URL, getIdToken } from '../firebase';

// 基礎 API 呼叫函式
async function apiCall(endpoint, options = {}) {
  const url = `${FUNCTIONS_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: `HTTP ${response.status}: ${response.statusText}`,
    }));
    throw new Error(error.error || error.message || '請求失敗');
  }

  return response.json();
}

// 帶認證的 API 呼叫函式
async function authApiCall(endpoint, options = {}) {
  const token = await getIdToken();

  const headers = {
    'Content-Type': 'application/json; charset=utf-8',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${FUNCTIONS_URL}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: `HTTP ${response.status}: ${response.statusText}`,
    }));
    throw new Error(error.error || error.message || '請求失敗');
  }

  return response.json();
}

// ==================== 專案 API ====================

/**
 * 獲取專案列表
 * @param {Object} params - 查詢參數
 * @param {string} params.status - 專案狀態
 * @param {string} params.project_type - 專案類型
 * @param {number} params.year - 年份
 * @param {number} params.quarter - 季度
 * @param {string} params.owner - 擁有者
 * @param {string} params.search - 關鍵字搜尋
 * @param {string} params.sort - 排序 (例: '-created_at')
 * @param {number} params.limit - 每頁數量
 * @param {number} params.offset - 偏移量
 */
export const getProjects = async (params = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([_, v]) => v !== undefined && v !== ''),
  ).toString();
  return authApiCall(`/api/projects${query ? '?' + query : ''}`);
};

/**
 * 獲取單一專案詳情
 * @param {string} id - 專案 ID
 * @param {Object} options - 選項
 * @param {boolean} options.include_stats - 是否包含統計資料
 */
export const getProject = async (id, options = {}) => {
  const query = new URLSearchParams(options).toString();
  return authApiCall(`/api/projects/${id}${query ? '?' + query : ''}`);
};

/**
 * 建立新專案
 * @param {Object} data - 專案資料
 */
export const createProject = async (data) => {
  return authApiCall('/api/projects', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * 更新專案
 * @param {string} id - 專案 ID
 * @param {Object} data - 更新資料
 */
export const updateProject = async (id, data) => {
  return authApiCall(`/api/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * 刪除專案
 * @param {string} id - 專案 ID
 * @param {Object} options - 選項
 * @param {boolean} options.hard_delete - 是否硬刪除
 */
export const deleteProject = async (id, options = {}) => {
  const query = new URLSearchParams(options).toString();
  return authApiCall(`/api/projects/${id}${query ? '?' + query : ''}`, {
    method: 'DELETE',
  });
};

/**
 * 複製專案
 * @param {string} id - 原專案 ID
 * @param {Object} data - 新專案資料
 */
export const duplicateProject = async (id, data) => {
  return authApiCall(`/api/projects/${id}/duplicate`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * 獲取專案統計（強制刷新）
 * @param {string} id - 專案 ID
 */
export const getProjectStats = async (id) => {
  return authApiCall(`/api/projects/${id}/stats`);
};

// ==================== 專案範本 API 已移除 ====================
// 專案模板功能已移除，使用 5 步驟精靈建立專案

// ==================== 專案比較 API ====================

/**
 * 比較多個專案
 * @param {Object} params - 查詢參數
 * @param {string[]} params.project_ids - 專案 ID 陣列
 * @param {string} params.metrics - 比較指標 (預設: 'all')
 */
export const compareProjects = async (params) => {
  const query = new URLSearchParams();

  if (Array.isArray(params.project_ids)) {
    params.project_ids.forEach((id) => query.append('project_ids', id));
  }

  if (params.metrics) {
    query.append('metrics', params.metrics);
  }

  return authApiCall(`/api/projects/comparison?${query.toString()}`);
};

/**
 * 獲取專案趨勢分析
 * @param {Object} params - 查詢參數
 * @param {string} params.project_type - 專案類型
 * @param {number} params.start_year - 開始年份
 * @param {number} params.end_year - 結束年份
 * @param {string} params.metric - 比較指標
 */
export const getProjectTrends = async (params) => {
  const query = new URLSearchParams(params).toString();
  return authApiCall(`/api/projects/comparison/trends?${query}`);
};

// ==================== 輔助函數 ====================

/**
 * 專案狀態選項
 */
export const PROJECT_STATUSES = [
  { value: 'draft', label: '草稿' },
  { value: 'planning', label: '規劃中' },
  { value: 'active', label: '進行中' },
  { value: 'in_progress', label: '執行中' },
  { value: 'completed', label: '已完成' },
  { value: 'archived', label: '已封存' },
  { value: 'cancelled', label: '已取消' },
];

/**
 * 專案類型選項
 */
export const PROJECT_TYPES = [
  { value: 'annual', label: '年度' },
  { value: 'quarterly', label: '季度' },
  { value: 'semi-annual', label: '半年度' },
  { value: 'ad-hoc', label: '臨時' },
  { value: 'campaign', label: '專案活動' },
];

/**
 * 取得狀態標籤
 * @param {string} status - 狀態值
 */
export const getStatusLabel = (status) => {
  const item = PROJECT_STATUSES.find((s) => s.value === status);
  return item ? item.label : status;
};

/**
 * 取得類型標籤
 * @param {string} type - 類型值
 */
export const getTypeLabel = (type) => {
  const item = PROJECT_TYPES.find((t) => t.value === type);
  return item ? item.label : type;
};

/**
 * 格式化時間範圍顯示
 * @param {Object} timeScope - 時間範圍物件
 */
export const formatTimeScope = (timeScope) => {
  if (!timeScope) return '';

  const { year, quarter, half, start_date, end_date } = timeScope;

  if (quarter) return `${year} Q${quarter}`;
  if (half) return `${year} H${half}`;
  if (start_date && end_date) return `${start_date} ~ ${end_date}`;
  return `${year}年`;
};

/**
 * 取得專案進度百分比
 * @param {Object} project - 專案物件
 */
export const getProjectProgress = (project) => {
  if (!project || !project.stats) return 0;
  return project.stats.completion_rate || 0;
};

/**
 * 取得專案狀態顏色
 * @param {string} status - 狀態值
 */
export const getStatusColor = (status) => {
  const colors = {
    draft: 'gray',
    planning: 'blue',
    active: 'green',
    in_progress: 'yellow',
    completed: 'teal',
    archived: 'gray',
    cancelled: 'red',
  };
  return colors[status] || 'gray';
};

// ==================== 父子專案關係 API 已移除 ====================
// 父子專案功能已移除
